import { useQueryClient } from "@tanstack/react-query";
import { set } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

import { ButtonStandard, Text } from "@performyard/py-components";
import { WorkspaceModelJson } from "@performyard/service-contracts";

import EmployeePicker, { SingleEmployee } from "../../../../../components/EmployeePicker";
import DatePicker from "../../../../../components/Inputs/DatePicker";
import InputWrapper from "../../../../../components/Inputs/InputWrapper";
import ContentModal from "../../../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../../../components/Spacer";
import { StaticToast } from "../../../../../components/Toast";

import { useEditMeetingActionItem } from "../../../../../api/hooks";
import { useEmployeePickerSearch } from "../../../../../api/hooks/defs/employees";
import useForm from "../../../../../hooks/useForm";
import useToast from "../../../../../hooks/useToast";

import { AssignmentValidationErrors, assignmentFormSchema } from "../../schema";

interface UserAssignmentModalProps {
	isOpen: boolean;
	onClose: () => void;
	assignmentContext: {
		actionItemId: string;
		meetingId: string;
		currentAssigneeId?: string;
		dueDate?: Date | null;
		name?: string;
	} | null;
	assignerId: string;
	workspace: WorkspaceModelJson;
}

const UserAssignmentModal = ({
	isOpen,
	onClose,
	assignmentContext,
	assignerId,
	workspace,
}: UserAssignmentModalProps) => {
	const { mutate: editActionItem, isPending: isEditActionItemPending } = useEditMeetingActionItem();
	const toast = useToast();
	const queryClient = useQueryClient();
	const hasInitialized = useRef(false);
	const [validationErrors, setValidationErrors] = useState<AssignmentValidationErrors>({});

	const workspaceParticipantIds = workspace.participants.map((participant) => participant.userId);

	const { employeeData } = useEmployeePickerSearch(
		"employee-by-id",
		{
			searchTerm: "",
			excludeCurrentUser: false,
			exclusionList: [],
		},
		null,
		"searchService",
		{ id_in: workspaceParticipantIds }
	);

	// Initialize form with useForm hook
	const { validate, updateForm, state } = useForm({
		schema: assignmentFormSchema,
		defaultValues: {
			assignee: null,
			dueDate: null,
		},
		disableWarning: true,
	});

	// Reset form when modal opens/closes or assignmentContext changes
	useEffect(() => {
		if (isOpen && assignmentContext && !hasInitialized.current) {
			// Update form with fetched data once available
			if (employeeData?.length) {
				// Update assignee if there is one
				if (assignmentContext.currentAssigneeId) {
					const currentAssigneeData = employeeData.find(
						(emp) => emp.value === assignmentContext.currentAssigneeId
					);
					if (currentAssigneeData) {
						updateForm({ assignee: currentAssigneeData });
					}
				} else {
					updateForm({ assignee: null });
				}

				// Update due date if there is one
				if (assignmentContext.dueDate) {
					updateForm({
						dueDate: set(assignmentContext.dueDate, { hours: 17, minutes: 0, seconds: 0, milliseconds: 0 }),
					});
				} else {
					updateForm({ dueDate: null });
				}
			}

			hasInitialized.current = true;
		} else if (!isOpen) {
			hasInitialized.current = false;
		}
	}, [isOpen, assignmentContext, employeeData, updateForm]);

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setValidationErrors({});

		// Validate form
		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors: AssignmentValidationErrors = {};
			Object.entries(validationResult.errors || {}).forEach(([key, error]) => {
				errors[key as keyof AssignmentValidationErrors] = { message: error.message };
			});
			setValidationErrors(errors);
			return;
		}

		if (assignmentContext) {
			editActionItem(
				{
					actionItemId: assignmentContext.actionItemId,
					data: {
						participants: state.assignee
							? [
									{ userId: assignerId, role: "assigner" },
									{ userId: state.assignee.value, role: "assignee" },
								]
							: [],
						dueDate: state.dueDate ? state.dueDate.toISOString() : null,
					},
				},
				{
					onSuccess: () => {
						toast("Action item updated", { type: "success", size: "large" });
						queryClient.invalidateQueries({
							queryKey: ["get-meeting-action-items-by-meeting", assignmentContext.meetingId],
						});
						onClose();
					},
				}
			);
		}
	};

	const handleClose = () => {
		setValidationErrors({});
		onClose();
	};

	return (
		<ContentModal
			isOpen={isOpen}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					Assign Action Item
				</Text>
			}
			handleClose={handleClose}
			buttons={[
				<div key="cancelButton">
					<ButtonStandard size="large" onClick={handleClose} disabled={isEditActionItemPending}>
						Cancel
					</ButtonStandard>
				</div>,
				<div key="assignButton">
					<ButtonStandard
						size="large"
						color="green"
						type="submit"
						form="assignment-form"
						disabled={isEditActionItemPending}
					>
						Assign
					</ButtonStandard>
				</div>,
			]}
		>
			<form id="assignment-form" onSubmit={handleSubmit}>
				<EmployeePicker
					value={state.assignee || ""}
					onChange={(val) => updateForm({ assignee: val as SingleEmployee | null })}
					disabled={isEditActionItemPending}
					isClearable={true}
					filters={{ id_in: workspaceParticipantIds.join(",") }}
					version="searchService"
					searchServiceProps={{ id_in: workspaceParticipantIds }}
					label="Assignee"
					errorMessage={validationErrors.assignee?.message}
				/>
				<Spacer height={2} />
				<InputWrapper label="Due Date" errorMessage={validationErrors.dueDate?.message}>
					<DatePicker
						value={state.dueDate || ""}
						onChange={(date) => {
							if (date) {
								updateForm({
									dueDate: set(date, { hours: 17, minutes: 0, seconds: 0, milliseconds: 0 }),
								});
							} else {
								updateForm({ dueDate: null });
							}
						}}
						disabled={isEditActionItemPending}
						clearable={true}
						hasIcon={true}
					/>
				</InputWrapper>
				{state.dueDate && (
					<>
						<Spacer height={2} />
						<div className="flex items-center justify-center">
							<StaticToast
								message="Due Date is set for 5:00pm in your current time zone"
								type="info"
								size="small"
							/>
						</div>
					</>
				)}
			</form>
		</ContentModal>
	);
};

export default UserAssignmentModal;
